import {Component, OnInit, ViewChild} from '@angular/core';
import {map, takeUntil} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {ActivatedRoute} from '@angular/router';
import {AccountAdminService} from '../../services/api/account-admin.service';
import {AdminNavigationService} from '../../services/admin-navigation.service';
import {AdminReportGroupFilter} from '../../viewmodels/adminReportGroupFilter';
import {BaseComponent} from '../base.component';
import {LoadingState, NotificationService} from 'common';
import {MatDatepickerInputEvent} from '@angular/material/datepicker';
import {ProtocolFindProductionLogsBodyDto} from '../../dtos/protocolFindProductionLogsBodyDto';
import {ProtocolFindProductionLogsResponseDto} from '../../dtos/protocolFindProductionLogsResponseDto';
import {ProtocolFindProductionLogsViewModel} from '../../viewmodels/protocolFindProductionLogsViewModel';
import {MatDialog} from '@angular/material/dialog';
import {ProtocolFindProductionLogsDataWrapper} from '../../wrappers/protocolFindProductionLogsDataWrapper';
import {TableCellInfoWrapper} from '../../wrappers/tableCellInfoWrapper';
import {DialogShowDetailedMessageComponent} from '../dialogs/dialog-show-detailed-message';
import {ProtocolFindProductionLogsSummaryViewModel} from '../../viewmodels/protocolFindProductionLogsSummaryViewModel';
import {BigQueryAdminService} from '../../services/api/big-query-admin-service';
import {DateFormat, Dates} from '../../../../../common/src/lib/utils/Dates';
import {LastSyncBigQueryDataWrapper} from '../../wrappers/lastSyncBigQueryDataWrapper';
import {removeWhitespaces} from '../../utils/admin-utils';

@Component({
    selector: 'app-serial-code',
    templateUrl: './find-sensor-production-logs.component.html',
    styleUrls: ['./find-sensor-production-logs.component.scss']
})
export class FindSensorProductionLogsComponent extends BaseComponent implements OnInit {
    public readonly searchModes: typeof SearchMode = SearchMode;
    public readonly products: typeof Product = Product;
    public readonly applications: typeof Application = Application;
    private readonly VIEW_SURROUNDING_MAX_DATES_TO_SHOW = 5;

    public showSearchSerialCode: boolean;
    public showSearchAccountEmail: boolean;
    public readonly detailedDataSource: MatTableDataSource<ProtocolFindProductionLogsViewModel>;
    public detailedColumns: Map<string, string>;

    public readonly summaryDataSource: MatTableDataSource<ProtocolFindProductionLogsSummaryViewModel>;
    public summaryColumns: Map<string, string>;
    public detailedSourceData: ProtocolFindProductionLogsViewModel[];

    public loadingState: LoadingState;
    public filter: AdminReportGroupFilter;
    public truncateColumns = ['message'];

    public searchMode: SearchMode;

    @ViewChild(MatSort)
    public sort: MatSort;

    public searchValueSerialCode: string;
    public searchHintSerialCode: string;

    public searchValueAccountEmail: string;
    public searchHintAccountEmail: string;

    public readonly minLength = 5;

    public emailToViewSurroundingLogs: string;

    public isLastSearchWithAccountEmail: boolean;

    public fromDateSerialCodeSearch: Date;
    public toDateSerialCodeSearch: Date;
    public fromDateAccountEmailSearch: Date;
    public toDateAccountEmailSearch: Date;

    public detailedSummaryTitle: string;

    public applicationProductMap = new Map<string, string>();

    constructor(private bigQueryAdminService: BigQueryAdminService,
                private accountAdminService: AccountAdminService,
                private notificationService: NotificationService,
                private route: ActivatedRoute,
                public dialog: MatDialog,
                private adminNavigation: AdminNavigationService
    ) {
        super();
        this.detailedDataSource = new MatTableDataSource([]);
        this.summaryDataSource = new MatTableDataSource([]);

        this.searchValueSerialCode = '';
        this.searchHintSerialCode = 'Insert a serial code to search production logs';
        this.showSearchSerialCode = true;

        this.searchValueAccountEmail = '';
        this.searchHintAccountEmail = 'Insert an email to search production logs';
        this.showSearchAccountEmail = true;

        this.detailedSummaryTitle = '';
        this.emailToViewSurroundingLogs = '';

        this.isLastSearchWithAccountEmail = false;

        this.applicationProductMap.set(Application.PHYSIO.toString() + Product.ANDROID.toString(), 'Calculating ...');
        this.applicationProductMap.set(Application.PHYSIO.toString() + Product.IOS.toString(), 'Calculating ...');

        this.fromDateSerialCodeSearch = new Date(
            new Date().getFullYear(),
            new Date().getMonth() - 2,
            new Date().getDate()
        );
        this.toDateSerialCodeSearch = new Date();
        this.fromDateAccountEmailSearch = new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getDate() - 3
        );
        this.toDateAccountEmailSearch = new Date();
        this.searchMode = SearchMode.SUMMARY;

        this.detailedColumns = new Map<string, string>();
        this.detailedColumns.set('position', 'No');
        this.detailedColumns.set('message', 'Message');
        this.detailedColumns.set('timestampDate', 'Date');
        this.detailedColumns.set('email', 'Email');
        this.detailedColumns.set('product', 'Product');
        this.detailedColumns.set('productVersion', 'Product Version');
        this.detailedColumns.set('application', 'Application');
        this.detailedColumns.set('applicationVersion', 'Application Version');
        this.detailedColumns.set('deviceModel', 'Device Model');

        this.summaryColumns = new Map<string, string>();
        this.summaryColumns.set('position', 'No');
        this.summaryColumns.set('message', 'Message');
        this.summaryColumns.set('hits', 'Hits');
        this.summaryColumns.set('timestampTimeRange', 'Date Range');
        this.summaryColumns.set('emails', 'Emails');
        this.summaryColumns.set('products', 'Products');
        this.summaryColumns.set('productVersions', 'Product Versions');
        this.summaryColumns.set('applications', 'Applications');
        this.summaryColumns.set('applicationVersions', 'Application Versions');
        this.summaryColumns.set('deviceModels', 'Device Models');

        this.filter = new AdminReportGroupFilter();
        this.filter.supportsQuery = false;
        this.filter.supportsSubscriptionPlanTypes = false;
        this.filter.supportsSubscriptionPeriodUnits = false;
        this.filter.supportsSubscriptionSources = false;
    }

    ngOnInit(): void {
        this.analyticsSwitchPage('Admin-FindSensorProductionLogsComponent', '', '/admin/findSensorProductionLogs');

        // Query last sync usage.
        this.queryLastSyncUsage();
    }

    onSubmitSerialCodeSearch(): void {
        this.search( false);
    }

    onSubmitAccountEmailSearch(): void {
        this.search(true);
    }

    onBackToSummaryLogs(): void {
        this.searchMode = SearchMode.SUMMARY;
        this.detailedSummaryTitle = '';
        this.refreshDetailedDataSource();
    }

    onSerialCodeValueChanged(value: string): void {
        const cleanValue = removeWhitespaces(value);

        this.searchValueSerialCode = cleanValue.toUpperCase();
    }

    onAccountEmailValueChanged(value: string): void {
        const cleanValue = removeWhitespaces(value);

        this.searchValueAccountEmail = cleanValue.toUpperCase();
    }

    canSearchSerialCode(): boolean {
        return this.showSearchSerialCode && this.searchValueSerialCode?.length >= this.minLength &&
            this.fromDateSerialCodeSearch !== null &&
            this.toDateSerialCodeSearch !== null;
    }

    canSearchAccountEmail(): boolean {
        return this.showSearchAccountEmail && this.searchValueAccountEmail?.length >= this.minLength &&
            this.fromDateAccountEmailSearch !== null &&
            this.toDateAccountEmailSearch !== null;
    }

    onFromDateSerialCodeSearchChanged = (event: MatDatepickerInputEvent<unknown, unknown | null>) => {
        if (!event.value) {
            return;
        }

        this.fromDateSerialCodeSearch = new Date(event.value.toString());
    }

    onToDateSerialCodeSearchChanged = (event: MatDatepickerInputEvent<unknown, unknown | null>) => {
        if (!event.value) {
            return;
        }
        const selectedEndOfDateTime = new Date(event.value.toString()).setHours(23, 59, 59, 999);

        this.toDateSerialCodeSearch = new Date(selectedEndOfDateTime);
    }

    onFromDateAccountEmailSearchChanged = (event: MatDatepickerInputEvent<unknown, unknown | null>) => {
        if (!event.value) {
            return;
        }
        this.fromDateAccountEmailSearch = new Date(event.value.toString());
    }

    onToDateAccountEmailSearchChanged = (event: MatDatepickerInputEvent<unknown, unknown | null>) => {
        if (!event.value) {
            return;
        }
        const selectedEndOfDateTime = new Date(event.value.toString()).setHours(23, 59, 59, 999);

        this.toDateAccountEmailSearch = new Date(selectedEndOfDateTime);
    }

    private search(accountEmailSearch?: boolean): void {
        this.loadingState = LoadingState.LOADING;
        this.showSearchSerialCode = false;
        this.showSearchAccountEmail = false;
        this.searchMode = SearchMode.SUMMARY;

        const bigQueryObservable = accountEmailSearch ?
            this.bigQueryAdminService.findProductionLogsForAccount(this.getBigQueryBodyDto(accountEmailSearch)) :
            this.bigQueryAdminService.findProductionLogsForSerialCode(this.getBigQueryBodyDto(accountEmailSearch));

        bigQueryObservable
                .pipe(takeUntil(this.destroySubject))
                .pipe(map((progressDto: ProtocolFindProductionLogsResponseDto) => {
                    const dataWrappers: ProtocolFindProductionLogsDataWrapper[] = [];

                    Object.values(progressDto.dataRows).forEach((dataRow, index) => {
                        dataWrappers.push(new ProtocolFindProductionLogsDataWrapper(index, dataRow.product, dataRow.application, dataRow.timestamp,
                            dataRow.message, dataRow.accountCode, dataRow.email, dataRow.deviceModel,
                            dataRow.productVersion, dataRow.applicationVersion));
                    });

                    return dataWrappers;
                }))
                .subscribe(results => {
                    this.detailedSourceData = ProtocolFindProductionLogsViewModel.from(results);
                    this.detailedDataSource.data = this.detailedSourceData;
                    this.summaryDataSource.data = ProtocolFindProductionLogsSummaryViewModel.from(results);

                    this.isLastSearchWithAccountEmail = accountEmailSearch;
                    if (!accountEmailSearch) {
                        this.emailToViewSurroundingLogs = this.detailedSourceData.length > 0 ?
                            this.detailedSourceData[0].email :
                            '';
                    }
                    this.loadingState = LoadingState.LOADED;
                    this.showSearchSerialCode = true;
                    this.showSearchAccountEmail = true;

                }, error => {
                    this.showSearchSerialCode = true;
                    this.showSearchAccountEmail = true;
                    this.loadingState = LoadingState.ERROR;
                    if (accountEmailSearch) {
                        if (error.error !== undefined && error.error.errorCode === 'ERR_1002' && error.status === 404) {
                            this.notificationService.info(`Could not find account for email ` + this.searchValueAccountEmail);
                        } else if (error.status === 401) {
                            this.notificationService.info(`Your login has expired. Please login again`);
                        }
                    } else {
                        this.notificationService.info(`Couldn't load serial code info`);
                    }

                    this.errorHandler.handle(error);
                });
    }

    openDialog(message: string): void {
        const formattedMessage = message.split('}}').join('\n');
        this.dialog.open(DialogShowDetailedMessageComponent, {
            data: {message: formattedMessage, title: 'Message'},
        });
    }

    onCellClick(cell: TableCellInfoWrapper): void {
        if (cell.name === 'message') {
            this.openDialog(cell.value);
        } else if (cell.name === 'userCode') {
            this.adminNavigation.goToAccount(cell.value)
                .then(() => Promise.resolve())
                .catch(err => this.notification.info(`go to account Failed: ${err.statusText} (${err.status})`));
        }
    }

    onSearchModeChipSelected(mode: SearchMode): void {
        this.searchMode = mode;

        this.refreshDetailedDataSource();
    }

    refreshDetailedDataSource(): void {
        if (this.detailedDataSource.data.length === 0) {
            return;
        }

        if (this.searchMode === SearchMode.SUMMARY_DETAILED_MESSAGE) {
            this.detailedDataSource.data = this.detailedSourceData.filter(viewModel =>
                this.detailedSummaryTitle === viewModel.message.substring(0, ProtocolFindProductionLogsSummaryViewModel.SUMMARY_MESSAGE_LENGTH));
        } else {
            this.detailedDataSource.data = this.detailedSourceData;
        }
    }

    getMostRecentDatesOfSummaryLogs(): number[] {
        // Step 1: Extract unique date strings
        const uniqueDateStrings = [...new Set(this.detailedSourceData.map(viewModel => new Date(viewModel.timestampDate).toDateString()))];

        // Step 2: Create an array of unique date timestamps sorted by most recent
        return uniqueDateStrings
            .map(dateString => new Date(dateString).getTime())
            .sort((a, b) => b - a)
            .slice(0, this.VIEW_SURROUNDING_MAX_DATES_TO_SHOW);
    }

    getEmailsSummaryLogs(): string[] {
        return [...new Set(this.detailedSourceData.map(viewModel => viewModel.email))];
    }

    onEmailToViewSurroundingLogsPressed(email: string): void {
        this.emailToViewSurroundingLogs = email;
    }

    showViewSurroundingLogs(): boolean {
        return (this.searchMode === SearchMode.SUMMARY || this.searchMode === SearchMode.SUMMARY_DETAILED_MESSAGE) &&
            this.detailedDataSource.data.length !== 0 &&
            !this.isLastSearchWithAccountEmail &&
            this.showSearchSerialCode;
    }

    getFormattedDate(time: number): string {
        return Dates.format(new Date(time), DateFormat.DATE_LONG);
    }

    onViewSurroundingLogsOfDayClick(time: number): void {
        const selectedDateStartOfDay = new Date(time).setHours(0, 0, 0, 0);
        const selectedDateEndOfDay = new Date(time).setHours(23, 59, 59, 999);

        this.fromDateAccountEmailSearch = new Date(selectedDateStartOfDay);
        this.toDateAccountEmailSearch = new Date(selectedDateEndOfDay);
        this.searchValueAccountEmail = this.emailToViewSurroundingLogs;
        this.isLastSearchWithAccountEmail = true;

        this.search(true);
    }

    getBigQueryBodyDto(accountEmail: boolean): ProtocolFindProductionLogsBodyDto {
        const bodyDto = new ProtocolFindProductionLogsBodyDto();
        if (accountEmail) {
            bodyDto.startDate = this.fromDateAccountEmailSearch.getTime();
            bodyDto.endDate = this.toDateAccountEmailSearch.getTime();
            bodyDto.email = this.searchValueAccountEmail;
        } else {
            bodyDto.startDate = this.fromDateSerialCodeSearch.getTime();
            bodyDto.endDate = this.toDateSerialCodeSearch.getTime();
            bodyDto.messageQuery = this.searchValueSerialCode;
        }

        return bodyDto;
    }

    queryLastSyncUsage(): void {
        this.bigQueryAdminService.findGoogleLastSync()
            .pipe(takeUntil(this.destroySubject))
            .pipe(map((progressDto: ProtocolFindProductionLogsResponseDto) => {
                const dataWrappers: LastSyncBigQueryDataWrapper[] = [];

                Object.values(progressDto.dataRows).forEach((dataRow, index) => {
                    dataWrappers.push(new LastSyncBigQueryDataWrapper(dataRow.product, dataRow.application, dataRow.timestamp));
                });

                return dataWrappers;
            }))
            .subscribe(results => {
                results.map(res => {
                    this.applicationProductMap.set(res.application + res.product, this.getFormattedDateTime(res.timestampDate));
                }, error => {
                    this.applicationProductMap.forEach((value, key, map) => {

                        // Set the new value in the Map
                        map.set(key, 'Error');
                    });
                });
            });
    }

     // Format the date and time
    getFormattedDateTime(timestamp: number): string {
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: false, // Use 24-hour clock
            timeZone: 'Europe/Athens' // Greece time zone
        });
    }

    onSummaryRowClicked(row: ProtocolFindProductionLogsSummaryViewModel): void {
        this.searchMode = SearchMode.SUMMARY_DETAILED_MESSAGE;
        this.detailedSummaryTitle = row.message;
        this.refreshDetailedDataSource();
    }
}

export enum SearchMode {
    SUMMARY,
    DETAILED,
    SUMMARY_DETAILED_MESSAGE
}

enum Application {
    PHYSIO = 'PHYSIO'
}

enum Product {
    IOS = 'IOS',
    ANDROID = 'ANDROID'
}

